import React, { useState } from 'react';
import { MailIcon, MessageCircleIcon } from 'lucide-react';
import { commodities } from '../data/products';
import { buildOrderEmail, OrderEnquiry, whatsappLink } from '../utils/contact';
const incotermOptions = ['CIF', 'FOB', 'FCA', 'DDP', 'EXW'];
const initial: OrderEnquiry = {
  commodity: commodities[2].name,
  quantity: '',
  destination: '',
  incoterm: 'CIF',
  name: '',
  organisation: '',
  email: '',
  phone: '',
  message: ''
};
const fieldClass = 'w-full rounded-sm border border-hair bg-ink px-3.5 py-3 text-sm text-bone placeholder:text-muted/70 focus:border-gold focus:outline-none';
const labelClass = 'block font-mono text-[10px] uppercase tracking-[0.18em] text-muted';
export function OrderForm() {
  const [enquiry, setEnquiry] = useState<OrderEnquiry>(initial);
  const [submitted, setSubmitted] = useState(false);
  const set = (key: keyof OrderEnquiry) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setEnquiry(prev => ({
    ...prev,
    [key]: e.target.value
  }));
  const whatsappMessage = ['Hello Lumumbashi Minerals, I would like to place an order.', `Commodity: ${enquiry.commodity}`, enquiry.quantity ? `Quantity: ${enquiry.quantity}` : '', enquiry.destination ? `Destination: ${enquiry.destination}` : '', `Incoterm: ${enquiry.incoterm}`].filter(Boolean).join('\n');
  return <form className="border border-hair bg-panel p-6 sm:p-8" onSubmit={e => {
    e.preventDefault();
    window.location.href = buildOrderEmail(enquiry);
    setSubmitted(true);
  }} data-id="element-177" data-mp="3freay0vyzq69">
      <h2 className="font-display text-2xl text-bone" data-id="element-178" data-mp="3pmxwbtcwr6gr">Order enquiry</h2>
      <p className="mt-2 text-sm text-muted" data-id="element-179" data-mp="3uon5f3gkla27">
        Complete the fields and the button opens a pre-filled email to our trading desk. A trader
        replies with a firm offer, specification sheet and draft contract within 24 hours.
      </p>

      <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-180" data-mp="19zh8461luan0">
        <div className="sm:col-span-2" data-id="element-181" data-mp="35zgechj7i96l">
          <label className={labelClass} htmlFor="commodity" data-id="element-182" data-mp="362lyrjgw4mvz">
            Commodity
          </label>
          <select id="commodity" value={enquiry.commodity} onChange={set('commodity')} className={`${fieldClass} mt-2`} data-id="element-183" data-mp="313smtvh953xx">
            {commodities.map(c => <option key={c.id} value={c.name} data-id="element-184" data-mp="3b0xc446e9fiy">
                {c.name}
              </option>)}
          </select>
        </div>

        <div data-id="element-185" data-mp="2n7ngo9t6zzsj">
          <label className={labelClass} htmlFor="quantity" data-id="element-186" data-mp="2ib6u3k1n5thk">
            Quantity required
          </label>
          <input id="quantity" required value={enquiry.quantity} onChange={set('quantity')} placeholder="e.g. 500 t / 25 kg / 800 ct" className={`${fieldClass} mt-2`} data-id="element-187" data-mp="2w2v6zpjpslzl" />
        </div>

        <div data-id="element-188" data-mp="23jy1d22fzs07">
          <label className={labelClass} htmlFor="destination" data-id="element-189" data-mp="1zkfc5u3l8urk">
            Destination port or city
          </label>
          <input id="destination" required value={enquiry.destination} onChange={set('destination')} placeholder="e.g. Shanghai, China" className={`${fieldClass} mt-2`} data-id="element-190" data-mp="1pfz07bt8bxt1" />
        </div>

        <div data-id="element-191" data-mp="1fghuiudz9jx2">
          <label className={labelClass} htmlFor="incoterm" data-id="element-192" data-mp="1khfdh96tahzm">
            Preferred incoterm
          </label>
          <select id="incoterm" value={enquiry.incoterm} onChange={set('incoterm')} className={`${fieldClass} mt-2`} data-id="element-193" data-mp="2879p5nfea0ia">
            {incotermOptions.map(option => <option key={option} value={option} data-id="element-194" data-mp="2d40c8upm9wd7">
                {option}
              </option>)}
          </select>
        </div>

        <div data-id="element-195" data-mp="237444q3mrc96">
          <label className={labelClass} htmlFor="organisation" data-id="element-196" data-mp="236vmkkwkbirn">
            Company
          </label>
          <input id="organisation" value={enquiry.organisation} onChange={set('organisation')} placeholder="Registered buyer name" className={`${fieldClass} mt-2`} data-id="element-197" data-mp="msvs3pghklbs" />
        </div>

        <div data-id="element-198" data-mp="1396a3ivdju9k">
          <label className={labelClass} htmlFor="name" data-id="element-199" data-mp="139fzb48lhxe7">
            Contact name
          </label>
          <input id="name" required value={enquiry.name} onChange={set('name')} placeholder="Full name" className={`${fieldClass} mt-2`} data-id="element-200" data-mp="y9tm2usroh5e" />
        </div>

        <div data-id="element-201" data-mp="1qxft6d17e3x0">
          <label className={labelClass} htmlFor="email" data-id="element-202" data-mp="1qxzapsmxyqai">
            Email
          </label>
          <input id="email" type="email" required value={enquiry.email} onChange={set('email')} placeholder="you@company.com" className={`${fieldClass} mt-2`} data-id="element-203" data-mp="1m075xy1779ow" />
        </div>

        <div className="sm:col-span-2" data-id="element-204" data-mp="5lo24a9jew6m">
          <label className={labelClass} htmlFor="phone" data-id="element-205" data-mp="5mpganlunysk">
            Phone / WhatsApp
          </label>
          <input id="phone" value={enquiry.phone} onChange={set('phone')} placeholder="+00 000 000 000" className={`${fieldClass} mt-2`} data-id="element-206" data-mp="ofjy9oe2s5d" />
        </div>

        <div className="sm:col-span-2" data-id="element-207" data-mp="2m7uielfnguwd">
          <label className={labelClass} htmlFor="message" data-id="element-208" data-mp="2m6t2o3novk8b">
            Specification, payment terms or shipment schedule
          </label>
          <textarea id="message" rows={4} value={enquiry.message} onChange={set('message')} placeholder="Grade requirements, monthly tonnage, inspection agent, payment instrument…" className={`${fieldClass} mt-2 resize-y`} data-id="element-209" data-mp="2r0nll4vgzv6c" />
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-210" data-mp="39xyjom5d8ik7">
        <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-gold-light" data-id="element-211" data-mp="39vcbqlekc11g">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-212" data-mp="17ig4aeo2t1ww" />
          Place Order by Email
        </button>
        <a href={whatsappLink(whatsappMessage)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-sm border border-[#25D366] px-6 py-3.5 text-sm font-semibold text-[#25D366] transition-colors duration-150 hover:bg-[#25D366] hover:text-[#0a2f16]" data-id="element-213" data-mp="13hmhjomddt5l">
          <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-214" data-mp="1qfr5i4t9q5ez" />
          Send on WhatsApp
        </a>
      </div>

      {submitted && <p role="status" className="mt-5 border-l-2 border-gold bg-gold/5 px-4 py-3 text-sm text-bone/90" data-id="element-215" data-mp="2073wku78srjy">
          Your email client should now be open with the enquiry drafted. If nothing appeared, write to
          sales@lumumbashiminerals.com or message the desk on WhatsApp.
        </p>}
    </form>;
}